import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Briefcase, Award, GraduationCap, Upload, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const UEFA_LEVELS = ["UEFA C", "UEFA B", "UEFA A", "UEFA Pro", "UEFA Elite Youth A"];

export default function SettingsPage() {
  const { user, checkAuth } = useAuth();
  const [profile, setProfile] = useState({
    name: "", bio: "", position: "", location: "", club: "",
    experience_years: "", skills: "", preferred_foot: "", height: "", weight: "", age: "",
    open_to_opportunities: false,
    sprint_speed: "", vo2_max: "", vertical_jump: "", agility_time: "", bench_press: "", endurance_score: "",
  });
  const [passwords, setPasswords] = useState({ current: "", new: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [experience, setExperience] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [verifiedCerts, setVerifiedCerts] = useState([]);
  const [savingExp, setSavingExp] = useState(false);
  const [savingCerts, setSavingCerts] = useState(false);
  const [uploadingCert, setUploadingCert] = useState(false);
  const [certUpload, setCertUpload] = useState({ name: "", level: "", file: null });

  const role = user?.role || "";
  const canHaveExperience = ["athlete", "coach", "scout", "club"].includes(role);
  const canHaveCertifications = ["coach", "scout", "club"].includes(role);
  const isCoach = role === "coach";

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || "", bio: user.bio || "", position: user.position || "",
        location: user.location || "", club: user.club || "",
        experience_years: user.experience_years || "", skills: user.skills?.join(", ") || "",
        preferred_foot: user.preferred_foot || "", height: user.height || "",
        weight: user.weight || "", age: user.age || "",
        open_to_opportunities: user.open_to_opportunities || false,
        sprint_speed: user.sprint_speed || "", vo2_max: user.vo2_max || "",
        vertical_jump: user.vertical_jump || "", agility_time: user.agility_time || "",
        bench_press: user.bench_press || "", endurance_score: user.endurance_score || "",
      });
      setExperience(Array.isArray(user.experience) ? user.experience : []);
      setCertifications(Array.isArray(user.certifications) ? user.certifications : []);
      setVerifiedCerts(Array.isArray(user.verified_certifications) ? user.verified_certifications : []);
    }
  }, [user]);

  const handleProfileSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...profile,
        skills: profile.skills ? profile.skills.split(",").map(s => s.trim()).filter(Boolean) : [],
        experience_years: profile.experience_years ? parseInt(profile.experience_years) : null,
        age: profile.age ? parseInt(profile.age) : null,
        open_to_opportunities: profile.open_to_opportunities,
        sprint_speed: profile.sprint_speed ? parseFloat(profile.sprint_speed) : null,
        vo2_max: profile.vo2_max ? parseFloat(profile.vo2_max) : null,
        vertical_jump: profile.vertical_jump ? parseFloat(profile.vertical_jump) : null,
        agility_time: profile.agility_time ? parseFloat(profile.agility_time) : null,
        bench_press: profile.bench_press ? parseFloat(profile.bench_press) : null,
        endurance_score: profile.endurance_score ? parseFloat(profile.endurance_score) : null,
      };
      await api.put("/users/profile", payload);
      await checkAuth();
      toast.success("Profile updated!");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const handlePasswordChange = async () => {
    if (passwords.new !== passwords.confirm) { toast.error("Passwords don't match"); return; }
    if (passwords.new.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.put("/users/password", { current_password: passwords.current, new_password: passwords.new });
      setPasswords({ current: "", new: "", confirm: "" });
      toast.success("Password updated!");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      await api.put("/users/profile-image", formData);
      await checkAuth();
      toast.success("Profile image updated!");
    } catch { toast.error("Failed to upload image"); }
  };

  // Experience handlers
  const addExperienceEntry = () => {
    setExperience([...experience, { _key: `exp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, title: "", organization: "", start_date: "", end_date: "", current: false, description: "" }]);
  };
  const updateExperienceEntry = (idx, field, value) => {
    const next = [...experience];
    next[idx] = { ...next[idx], [field]: value };
    setExperience(next);
  };
  const removeExperienceEntry = (idx) => {
    setExperience(experience.filter((_, i) => i !== idx));
  };
  const handleSaveExperience = async () => {
    setSavingExp(true);
    try {
      const cleaned = experience
        .filter(e => e.title?.trim() && e.organization?.trim())
        .map(({ _key, ...rest }) => rest);
      await api.put("/users/experience", { experience: cleaned });
      await checkAuth();
      toast.success("Experience saved!");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingExp(false); }
  };

  // Self-reported certifications handlers
  const addCertEntry = () => {
    setCertifications([...certifications, { _key: `cert-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, cert_name: "", cert_level: "", issuer: "", year: "" }]);
  };
  const updateCertEntry = (idx, field, value) => {
    const next = [...certifications];
    next[idx] = { ...next[idx], [field]: value };
    setCertifications(next);
  };
  const removeCertEntry = (idx) => {
    setCertifications(certifications.filter((_, i) => i !== idx));
  };
  const handleSaveCertifications = async () => {
    setSavingCerts(true);
    try {
      const cleaned = certifications
        .filter(c => c.cert_name?.trim())
        .map(({ _key, ...rest }) => rest);
      await api.put("/users/certifications", { certifications: cleaned });
      await checkAuth();
      toast.success("Certifications saved!");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingCerts(false); }
  };

  // Verified diploma upload
  const handleCertUpload = async (e) => {
    e.preventDefault();
    if (!certUpload.file || !certUpload.name.trim()) {
      toast.error("Please provide a certification name and file");
      return;
    }
    setUploadingCert(true);
    try {
      const fd = new FormData();
      fd.append("file", certUpload.file);
      fd.append("cert_name", certUpload.name);
      fd.append("cert_level", certUpload.level);
      await api.post("/users/certification-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document submitted for admin review!");
      setCertUpload({ name: "", level: "", file: null });
      const fileInput = document.getElementById("cert-doc-upload");
      if (fileInput) fileInput.value = "";
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploadingCert(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-0 py-6" data-testid="settings-page">
      <h1 className="text-2xl font-medium text-zinc-900 mb-1" style={{ fontFamily: 'Outfit' }}>Settings</h1>
      <p className="text-sm text-zinc-500 mb-8">Manage your profile and account preferences</p>

      {/* Profile Photo */}
      <section className="mb-8">
        <h2 className="text-sm font-semibold text-zinc-900 uppercase tracking-wide mb-4" style={{ fontFamily: 'Outfit' }}>Profile Photo</h2>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-[#074572] flex items-center justify-center text-white text-xl font-semibold">
            {user?.name?.split(" ").map(n => n[0]).join("").toUpperCase() || "U"}
          </div>
          <div>
            <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" id="avatar-upload" data-testid="settings-avatar-upload" />
            <label htmlFor="avatar-upload">
              <Button variant="outline" className="border-[#E6E6E6] text-[#074572] cursor-pointer" asChild>
                <span>Upload Photo</span>
              </Button>
            </label>
            <p className="text-xs text-zinc-400 mt-1">JPG, PNG. Max 5MB.</p>
          </div>
        </div>
      </section>

      <Separator className="bg-[#E6E6E6] mb-8" />

      {/* Profile Info */}
      <section className="mb-8">
        <h2 className="text-sm font-semibold text-zinc-900 uppercase tracking-wide mb-4" style={{ fontFamily: 'Outfit' }}>Profile Information</h2>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-zinc-600 text-xs">Full Name</Label><Input value={profile.name} onChange={e => setProfile({...profile, name: e.target.value})} className="mt-1 border-[#E6E6E6]" data-testid="settings-name" /></div>
            <div><Label className="text-zinc-600 text-xs">Position</Label><Input value={profile.position} onChange={e => setProfile({...profile, position: e.target.value})} className="mt-1 border-[#E6E6E6]" data-testid="settings-position" /></div>
          </div>
          <div><Label className="text-zinc-600 text-xs">Bio</Label><Textarea value={profile.bio} onChange={e => setProfile({...profile, bio: e.target.value})} className="mt-1 border-[#E6E6E6] min-h-[80px]" data-testid="settings-bio" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-zinc-600 text-xs">Location</Label><Input value={profile.location} onChange={e => setProfile({...profile, location: e.target.value})} className="mt-1 border-[#E6E6E6]" data-testid="settings-location" /></div>
            <div><Label className="text-zinc-600 text-xs">Club</Label><Input value={profile.club} onChange={e => setProfile({...profile, club: e.target.value})} className="mt-1 border-[#E6E6E6]" data-testid="settings-club" /></div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div><Label className="text-zinc-600 text-xs">Age</Label><Input type="number" value={profile.age} onChange={e => setProfile({...profile, age: e.target.value})} className="mt-1 border-[#E6E6E6]" data-testid="settings-age" /></div>
            <div><Label className="text-zinc-600 text-xs">Height</Label><Input value={profile.height} onChange={e => setProfile({...profile, height: e.target.value})} placeholder="e.g. 182cm" className="mt-1 border-[#E6E6E6]" data-testid="settings-height" /></div>
            <div><Label className="text-zinc-600 text-xs">Weight</Label><Input value={profile.weight} onChange={e => setProfile({...profile, weight: e.target.value})} placeholder="e.g. 78kg" className="mt-1 border-[#E6E6E6]" data-testid="settings-weight" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-zinc-600 text-xs">Experience (years)</Label><Input type="number" value={profile.experience_years} onChange={e => setProfile({...profile, experience_years: e.target.value})} className="mt-1 border-[#E6E6E6]" data-testid="settings-experience" /></div>
            <div>
              <Label className="text-zinc-600 text-xs">Preferred Foot</Label>
              <Select value={profile.preferred_foot} onValueChange={v => setProfile({...profile, preferred_foot: v})}>
                <SelectTrigger className="mt-1 border-[#E6E6E6]" data-testid="settings-foot-select">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Right">Right</SelectItem>
                  <SelectItem value="Left">Left</SelectItem>
                  <SelectItem value="Both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="text-zinc-600 text-xs">Skills (comma-separated)</Label><Input value={profile.skills} onChange={e => setProfile({...profile, skills: e.target.value})} placeholder="e.g. Passing, Dribbling, Leadership" className="mt-1 border-[#E6E6E6]" data-testid="settings-skills" /></div>
          <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
            <div>
              <p className="text-sm font-medium text-zinc-900">Open to Opportunities</p>
              <p className="text-xs text-zinc-500 mt-0.5">Let scouts and coaches know you're available</p>
            </div>
            <Switch
              checked={profile.open_to_opportunities}
              onCheckedChange={(v) => setProfile({...profile, open_to_opportunities: v})}
              data-testid="settings-opportunities-toggle"
            />
          </div>
          <Button onClick={handleProfileSave} disabled={saving} className="bg-[#F85037] hover:bg-[#e04530] text-white" data-testid="settings-save-btn">
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </section>

      <Separator className="bg-[#E6E6E6] mb-8" />

      {/* Ergometric Stats */}
      <section className="mb-8">
        <h2 className="text-sm font-semibold text-zinc-900 uppercase tracking-wide mb-4" style={{ fontFamily: 'Outfit' }}>Ergometric Stats</h2>
        <p className="text-xs text-zinc-500 mb-4">Add your physical performance metrics. Upload an official document to get verified.</p>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div><Label className="text-zinc-600 text-xs">Sprint Speed (km/h)</Label><Input type="number" step="0.1" value={profile.sprint_speed} onChange={e => setProfile({...profile, sprint_speed: e.target.value})} placeholder="e.g. 32.5" className="mt-1 border-[#E6E6E6]" data-testid="settings-sprint" /></div>
            <div><Label className="text-zinc-600 text-xs">VO2 Max (ml/kg/min)</Label><Input type="number" step="0.1" value={profile.vo2_max} onChange={e => setProfile({...profile, vo2_max: e.target.value})} placeholder="e.g. 58.2" className="mt-1 border-[#E6E6E6]" data-testid="settings-vo2" /></div>
            <div><Label className="text-zinc-600 text-xs">Vertical Jump (cm)</Label><Input type="number" step="0.1" value={profile.vertical_jump} onChange={e => setProfile({...profile, vertical_jump: e.target.value})} placeholder="e.g. 62.0" className="mt-1 border-[#E6E6E6]" data-testid="settings-jump" /></div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div><Label className="text-zinc-600 text-xs">Agility T-Test (sec)</Label><Input type="number" step="0.1" value={profile.agility_time} onChange={e => setProfile({...profile, agility_time: e.target.value})} placeholder="e.g. 4.1" className="mt-1 border-[#E6E6E6]" data-testid="settings-agility" /></div>
            <div><Label className="text-zinc-600 text-xs">Bench Press (kg)</Label><Input type="number" step="0.1" value={profile.bench_press} onChange={e => setProfile({...profile, bench_press: e.target.value})} placeholder="e.g. 85.0" className="mt-1 border-[#E6E6E6]" data-testid="settings-bench" /></div>
            <div><Label className="text-zinc-600 text-xs">Endurance Score (/100)</Label><Input type="number" step="0.1" value={profile.endurance_score} onChange={e => setProfile({...profile, endurance_score: e.target.value})} placeholder="e.g. 88.0" className="mt-1 border-[#E6E6E6]" data-testid="settings-endurance" /></div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-[#074572]/5 border border-[#074572]/10 rounded-lg">
            <div className="flex-1">
              <p className="text-xs text-[#074572] font-medium">Verify your ergometric stats</p>
              <p className="text-[10px] text-zinc-500 mt-0.5">Upload an official test document to get the verified badge</p>
            </div>
            <div>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={async (e) => { const file = e.target.files[0]; if (!file) return; const fd = new FormData(); fd.append("file", file); try { await api.put("/users/ergo-verify", fd); toast.success("Document uploaded for verification!"); } catch { toast.error("Upload failed"); } }} className="hidden" id="ergo-doc-upload" data-testid="settings-ergo-upload" />
              <label htmlFor="ergo-doc-upload">
                <Button variant="outline" className="border-[#074572]/20 text-[#074572] text-xs cursor-pointer" asChild><span>Upload Document</span></Button>
              </label>
            </div>
          </div>
        </div>
      </section>

      <Separator className="bg-[#E6E6E6] mb-8" />

      {/* Experience */}
      {canHaveExperience && (
        <>
          <section className="mb-8" data-testid="settings-experience-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Briefcase size={16} className="text-[#074572]" />
                <h2 className="text-sm font-semibold text-zinc-900 uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>Career & Experience</h2>
              </div>
              <Button type="button" size="sm" variant="outline" onClick={addExperienceEntry} className="border-[#E6E6E6] text-[#074572] gap-1.5 h-8" data-testid="add-experience-btn">
                <Plus size={14} /> Add
              </Button>
            </div>
            <p className="text-xs text-zinc-500 mb-4">Showcase your career history — clubs, roles, scouting positions, coaching tenures, etc.</p>
            {experience.length === 0 ? (
              <p className="text-xs text-zinc-400 text-center py-6 border border-dashed border-[#E6E6E6] rounded-md">No experience added yet. Click "Add" to create your first entry.</p>
            ) : (
              <div className="space-y-3">
                {experience.map((exp, idx) => (
                  <div key={exp._key || `exp-legacy-${idx}`} className="border border-[#E6E6E6] rounded-lg p-4 bg-zinc-50/50" data-testid={`experience-entry-${idx}`}>
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">Entry #{idx + 1}</span>
                      <button type="button" onClick={() => removeExperienceEntry(idx)} className="text-zinc-400 hover:text-[#F85037]" data-testid={`remove-experience-${idx}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-zinc-600 text-xs">Title / Role</Label><Input value={exp.title || ""} onChange={e => updateExperienceEntry(idx, "title", e.target.value)} placeholder="e.g. Head Scout" className="mt-1 border-[#E6E6E6]" data-testid={`exp-title-${idx}`} /></div>
                      <div><Label className="text-zinc-600 text-xs">Organization / Club</Label><Input value={exp.organization || ""} onChange={e => updateExperienceEntry(idx, "organization", e.target.value)} placeholder="e.g. Manchester United" className="mt-1 border-[#E6E6E6]" data-testid={`exp-org-${idx}`} /></div>
                      <div><Label className="text-zinc-600 text-xs">Start Date</Label><Input value={exp.start_date || ""} onChange={e => updateExperienceEntry(idx, "start_date", e.target.value)} placeholder="MM/YYYY" className="mt-1 border-[#E6E6E6]" data-testid={`exp-start-${idx}`} /></div>
                      <div>
                        <Label className="text-zinc-600 text-xs">End Date</Label>
                        <Input value={exp.end_date || ""} onChange={e => updateExperienceEntry(idx, "end_date", e.target.value)} placeholder="MM/YYYY" disabled={exp.current} className="mt-1 border-[#E6E6E6] disabled:opacity-50" data-testid={`exp-end-${idx}`} />
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <Switch checked={!!exp.current} onCheckedChange={(v) => updateExperienceEntry(idx, "current", v)} data-testid={`exp-current-${idx}`} />
                      <span className="text-xs text-zinc-600">I currently work here</span>
                    </div>
                    <div className="mt-3"><Label className="text-zinc-600 text-xs">Description (optional)</Label><Textarea value={exp.description || ""} onChange={e => updateExperienceEntry(idx, "description", e.target.value)} placeholder="Key achievements, responsibilities..." className="mt-1 border-[#E6E6E6] min-h-[60px]" data-testid={`exp-desc-${idx}`} /></div>
                  </div>
                ))}
                <Button onClick={handleSaveExperience} disabled={savingExp} className="bg-[#F85037] hover:bg-[#e04530] text-white" data-testid="save-experience-btn">
                  {savingExp ? "Saving..." : "Save Experience"}
                </Button>
              </div>
            )}
          </section>

          <Separator className="bg-[#E6E6E6] mb-8" />
        </>
      )}

      {/* Certifications */}
      {canHaveCertifications && (
        <>
          <section className="mb-8" data-testid="settings-certifications-section">
            <div className="flex items-center gap-2 mb-2">
              <Award size={16} className="text-[#074572]" />
              <h2 className="text-sm font-semibold text-zinc-900 uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>Certifications & Diplomas</h2>
            </div>
            <p className="text-xs text-zinc-500 mb-4">List your certifications. Upload an official document {isCoach ? "(e.g. UEFA Diploma)" : ""} to receive a verified badge after admin review.</p>

            {/* Prominent coach diploma status card */}
            {isCoach && (
              <div className={`mb-5 rounded-lg border p-4 ${verifiedCerts.length > 0 ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}`} data-testid="coach-diploma-card">
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${verifiedCerts.length > 0 ? "bg-green-100" : "bg-amber-100"}`}>
                    <GraduationCap size={18} className={verifiedCerts.length > 0 ? "text-green-700" : "text-amber-700"} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-zinc-900">Your coaching diploma</p>
                      {verifiedCerts.length > 0 ? (
                        <Badge className="text-[10px] bg-green-600 text-white gap-1"><ShieldCheck size={11} /> Verified</Badge>
                      ) : (
                        <Badge className="text-[10px] bg-amber-500 text-white">Not verified</Badge>
                      )}
                    </div>
                    <p className="text-xs text-zinc-600 mt-1">
                      Current licence: <span className="font-medium text-zinc-800">{user?.coaching_license || "not added yet"}</span>
                    </p>
                    <p className="text-xs text-zinc-500 mt-1.5 leading-relaxed">
                      {verifiedCerts.length > 0
                        ? "Your diploma has been reviewed and verified by SportsUnity."
                        : "Added a licence later or during signup? Upload your official diploma below and our admins will award you a verified badge."}
                    </p>
                    {verifiedCerts.length === 0 && (
                      <Button
                        type="button" size="sm"
                        onClick={() => document.getElementById("coach-diploma-upload")?.scrollIntoView({ behavior: "smooth", block: "center" })}
                        className="mt-3 bg-[#074572] hover:bg-[#053a60] text-white gap-1.5 h-8"
                        data-testid="coach-diploma-upload-cta"
                      >
                        <Upload size={13} /> Upload your coaching diploma
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Verified certs (read-only) */}
            {verifiedCerts.length > 0 && (
              <div className="mb-4">
                <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-2">Verified by SportsUnity</p>
                <div className="space-y-2">
                  {verifiedCerts.map((c, idx) => (
                    <div key={c.cert_name ? `vc-${c.cert_name}-${idx}` : `vc-${idx}`} className="flex items-center gap-2 px-3 py-2 bg-[#074572]/5 border border-[#074572]/10 rounded-md" data-testid={`verified-cert-row-${idx}`}>
                      <ShieldCheck size={14} className="text-[#074572] shrink-0" />
                      <span className="text-sm text-zinc-800 font-medium">{c.cert_name}</span>
                      {c.cert_level && <Badge className="text-[10px] bg-white border-[#074572]/20 text-[#074572]">{c.cert_level}</Badge>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Self-reported list */}
            <div className="flex items-center justify-between mb-3 mt-4">
              <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">Self-reported</p>
              <Button type="button" size="sm" variant="outline" onClick={addCertEntry} className="border-[#E6E6E6] text-[#074572] gap-1.5 h-7" data-testid="add-cert-btn">
                <Plus size={12} /> Add
              </Button>
            </div>
            {certifications.length === 0 ? (
              <p className="text-xs text-zinc-400 text-center py-6 border border-dashed border-[#E6E6E6] rounded-md">No self-reported certifications. Add one above.</p>
            ) : (
              <div className="space-y-3">
                {certifications.map((c, idx) => (
                  <div key={c._key || `cert-legacy-${idx}`} className="border border-[#E6E6E6] rounded-lg p-3 bg-zinc-50/50" data-testid={`cert-entry-${idx}`}>
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">Entry #{idx + 1}</span>
                      <button type="button" onClick={() => removeCertEntry(idx)} className="text-zinc-400 hover:text-[#F85037]" data-testid={`remove-cert-${idx}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-zinc-600 text-xs">Certification Name</Label><Input value={c.cert_name || ""} onChange={e => updateCertEntry(idx, "cert_name", e.target.value)} placeholder="e.g. UEFA Pro Licence" className="mt-1 border-[#E6E6E6]" data-testid={`cert-name-${idx}`} /></div>
                      <div>
                        <Label className="text-zinc-600 text-xs">Level</Label>
                        {isCoach ? (
                          <Select value={c.cert_level || ""} onValueChange={v => updateCertEntry(idx, "cert_level", v)}>
                            <SelectTrigger className="mt-1 border-[#E6E6E6]" data-testid={`cert-level-${idx}`}><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>{UEFA_LEVELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : (
                          <Input value={c.cert_level || ""} onChange={e => updateCertEntry(idx, "cert_level", e.target.value)} placeholder="e.g. Level 1" className="mt-1 border-[#E6E6E6]" data-testid={`cert-level-${idx}`} />
                        )}
                      </div>
                      <div><Label className="text-zinc-600 text-xs">Issuer</Label><Input value={c.issuer || ""} onChange={e => updateCertEntry(idx, "issuer", e.target.value)} placeholder="e.g. UEFA, FA" className="mt-1 border-[#E6E6E6]" data-testid={`cert-issuer-${idx}`} /></div>
                      <div><Label className="text-zinc-600 text-xs">Year</Label><Input value={c.year || ""} onChange={e => updateCertEntry(idx, "year", e.target.value)} placeholder="e.g. 2022" className="mt-1 border-[#E6E6E6]" data-testid={`cert-year-${idx}`} /></div>
                    </div>
                  </div>
                ))}
                <Button onClick={handleSaveCertifications} disabled={savingCerts} className="bg-[#F85037] hover:bg-[#e04530] text-white" data-testid="save-cert-btn">
                  {savingCerts ? "Saving..." : "Save Certifications"}
                </Button>
              </div>
            )}

            {/* Document upload for verification */}
            <div id="coach-diploma-upload" className="mt-6 p-4 bg-[#074572]/5 border border-[#074572]/10 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <GraduationCap size={16} className="text-[#074572]" />
                <p className="text-sm font-semibold text-[#074572]">Upload Diploma for Verification</p>
              </div>
              <p className="text-xs text-zinc-500 mb-3">Submit your official diploma document. Our admins will review it and award the verified badge.</p>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-zinc-600 text-xs">Certification Name</Label><Input value={certUpload.name} onChange={e => setCertUpload({...certUpload, name: e.target.value})} placeholder="e.g. UEFA A Diploma" className="mt-1 border-[#E6E6E6]" data-testid="cert-upload-name" /></div>
                  <div>
                    <Label className="text-zinc-600 text-xs">Level</Label>
                    {isCoach ? (
                      <Select value={certUpload.level} onValueChange={v => setCertUpload({...certUpload, level: v})}>
                        <SelectTrigger className="mt-1 border-[#E6E6E6]" data-testid="cert-upload-level"><SelectValue placeholder="Select UEFA Level" /></SelectTrigger>
                        <SelectContent>{UEFA_LEVELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <Input value={certUpload.level} onChange={e => setCertUpload({...certUpload, level: e.target.value})} placeholder="e.g. Senior" className="mt-1 border-[#E6E6E6]" data-testid="cert-upload-level" />
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setCertUpload({...certUpload, file: e.target.files[0] || null})} className="hidden" id="cert-doc-upload" data-testid="cert-doc-upload" />
                  <label htmlFor="cert-doc-upload">
                    <Button type="button" variant="outline" className="border-[#074572]/20 text-[#074572] gap-1.5 cursor-pointer" asChild>
                      <span><Upload size={14} /> {certUpload.file ? certUpload.file.name.substring(0, 24) : "Choose File"}</span>
                    </Button>
                  </label>
                  <Button onClick={handleCertUpload} disabled={uploadingCert || !certUpload.file || !certUpload.name.trim()} className="bg-[#074572] hover:bg-[#053a60] text-white" data-testid="submit-cert-btn">
                    {uploadingCert ? "Submitting..." : "Submit for Review"}
                  </Button>
                </div>
              </div>
            </div>
          </section>

          <Separator className="bg-[#E6E6E6] mb-8" />
        </>
      )}

      {/* Password */}
      <section className="mb-8">
        <h2 className="text-sm font-semibold text-zinc-900 uppercase tracking-wide mb-4" style={{ fontFamily: 'Outfit' }}>Change Password</h2>
        <div className="space-y-4">
          <div><Label className="text-zinc-600 text-xs">Current Password</Label><Input type="password" value={passwords.current} onChange={e => setPasswords({...passwords, current: e.target.value})} className="mt-1 border-[#E6E6E6]" data-testid="settings-current-pw" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-zinc-600 text-xs">New Password</Label><Input type="password" value={passwords.new} onChange={e => setPasswords({...passwords, new: e.target.value})} className="mt-1 border-[#E6E6E6]" data-testid="settings-new-pw" /></div>
            <div><Label className="text-zinc-600 text-xs">Confirm Password</Label><Input type="password" value={passwords.confirm} onChange={e => setPasswords({...passwords, confirm: e.target.value})} className="mt-1 border-[#E6E6E6]" data-testid="settings-confirm-pw" /></div>
          </div>
          <Button onClick={handlePasswordChange} variant="outline" className="border-[#E6E6E6] text-[#074572]" data-testid="settings-change-pw-btn">
            Update Password
          </Button>
        </div>
      </section>
    </div>
  );
}
